import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GraduationCap, LogIn, AlertCircle, Clock, BookOpen, Calendar } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import ErrorDisplay from '../../components/common/ErrorDisplay'
import { cbtService } from '../../services/cbtService'
import { useAuth } from '../../context/AuthContext'

const CBTLogin = () => {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    examId: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [examDetails, setExamDetails] = useState(null)
  const [step, setStep] = useState('login') // 'login', 'exam-selection', or 'exam-details'
  const [availableExams, setAvailableExams] = useState([])

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    setError('')
  }

  const fetchAvailableExams = async () => {
    try {
      const response = await cbtService.getAvailableExams()
      // Response structure: { success: true, data: exams }
      setAvailableExams(response.data?.data || [])
    } catch (err) {
      console.error('Failed to fetch available exams:', err)
      setAvailableExams([])
    }
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // First authenticate the user
      await login(formData.email, formData.password)
      
      // If examId is provided, validate access immediately
      if (formData.examId) {
        const validation = await cbtService.validateAccess(formData.examId)
        setExamDetails(validation.data)
        setStep('exam-details')
      } else {
        // If no examId, fetch available exams and show selection
        await fetchAvailableExams()
        setStep('exam-selection')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.')
    } finally {
      setLoading(false)
    }
  }

  const handleExamSelection = async (examId) => {
    setLoading(true)
    setError('')
    try {
      const validation = await cbtService.validateAccess(examId)
      console.log('Validation response:', validation)
      // Response structure: { data: { data: { allowed, exam, ... } } }
      setExamDetails(validation.data?.data || validation.data)
      setStep('exam-details')
    } catch (err) {
      console.error('Validation error:', err)
      setError(err.response?.data?.message || 'Failed to validate exam access')
      // Don't redirect on error, just show the error message
    } finally {
      setLoading(false)
    }
  }

  const handleEnterExam = async () => {
    if (!examDetails?.exam) return
    
    // Check if exam is already completed
    if (examDetails.alreadyCompleted) {
      setError('You have already completed this exam.')
      return
    }
    
    setLoading(true)
    try {
      // Login to CBT exam
      await cbtService.loginToExam(examDetails.exam.id)
      
      // Navigate to waiting room
      navigate(`/cbt/waiting/${examDetails.exam.id}`)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to enter exam')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'No date'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch (error) {
      return 'Invalid date'
    }
  }

  const formatTime = (timeString) => {
    if (!timeString) return 'No time'
    try {
      const [hours, minutes] = timeString.split(':')
      const date = new Date()
      date.setHours(parseInt(hours), parseInt(minutes))
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    } catch (error) {
      return 'Invalid time'
    }
  }

  if (step === 'exam-selection') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                <GraduationCap className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">Select Exam</CardTitle>
            <p className="text-gray-600 mt-2">Choose the exam you want to take</p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {error && <ErrorDisplay message={error} />}
            
            {availableExams.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">No CBT exams available at the moment.</p>
                <p className="text-sm text-gray-500 mt-2">Please contact your teacher or administrator.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {availableExams.map((exam) => (
                  <div key={exam.id} className="bg-white p-4 rounded-lg border hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{exam.title}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <BookOpen className="w-4 h-4" />
                            <span>{exam.subject?.name || 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>{exam.duration} minutes</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(exam.examDate)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>{formatTime(exam.startTime)}</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleExamSelection(exam.id)}
                        disabled={loading}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Select
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => setStep('login')}
                disabled={loading}
              >
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (step === 'exam-details' && examDetails?.exam) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                <GraduationCap className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">Exam Details</CardTitle>
            <p className="text-gray-600 mt-2">Review exam information before entering</p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {error && <ErrorDisplay message={error} />}
            
            {examDetails.alreadyCompleted && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm">
                  ✓ You have already completed this exam.
                </p>
              </div>
            )}
            
            <div className="bg-white p-6 rounded-lg border">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{examDetails.exam.title}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-gray-500" />
                  <span className="text-sm text-gray-600">Subject:</span>
                  <span className="font-medium">{examDetails.exam.subject?.name || 'N/A'}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-gray-500" />
                  <span className="text-sm text-gray-600">Duration:</span>
                  <span className="font-medium">{examDetails.exam.duration} minutes</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gray-500" />
                  <span className="text-sm text-gray-600">Date:</span>
                  <span className="font-medium">{formatDate(examDetails.exam.examDate)}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-gray-500" />
                  <span className="text-sm text-gray-600">Start Time:</span>
                  <span className="font-medium">{formatTime(examDetails.exam.startTime)}</span>
                </div>
              </div>
              
              {examDetails.exam.description && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mb-2">Description:</p>
                  <p className="text-gray-800">{examDetails.exam.description}</p>
                </div>
              )}
              
              {examDetails.exam.instructions && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mb-2">Instructions:</p>
                  <p className="text-gray-800">{examDetails.exam.instructions}</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-center space-x-4">
              <Button
                variant="outline"
                onClick={() => setStep('login')}
                disabled={loading}
              >
                Back to Login
              </Button>
              <Button
                onClick={handleEnterExam}
                disabled={loading || examDetails.alreadyCompleted}
                className={examDetails.alreadyCompleted ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}
              >
                {loading ? 'Entering...' : examDetails.alreadyCompleted ? 'Already Completed' : 'Enter Exam'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">Bridge of Life Academy</CardTitle>
          <p className="text-gray-600 mt-2">Computer-Based Testing Portal</p>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {error && <ErrorDisplay message={error} />}
            
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Enter your email"
                required
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Enter your password"
                required
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="examId">Exam ID (Optional)</Label>
              <Input
                id="examId"
                type="text"
                value={formData.examId}
                onChange={(e) => handleInputChange('examId', e.target.value)}
                placeholder="Enter exam ID if you have one"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                If you don't have an exam ID, you'll be redirected to your exam list
              </p>
            </div>
            
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Logging in...
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Login
                </>
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Need help? Contact your teacher or administrator
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default CBTLogin
